/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Loading } from 'uikit/loaders';

import ListEmpty from './listEmpty';

import styles from '../styles/infiniteScrollList.module.scss';

const InfiniteScrollList = ({
  components = {},
  emptyStateBody = '',
  emptyStateBodyVariant = 'b01',
  emptyStateButtonColor = 'brand',
  emptyStateButtonText = '',
  emptyStateHasAddIcon = true,
  emptyStateTitle = '',
  emptyStateTitleVariant = 'd02',
  handleEmptyStateClick = () => {},
  hasButton = true,
  queryEnabled = true,
  queryFn,
  queryKey,
}) => {
  const infiniteScrollQueryKey = Array.isArray(queryKey) ? queryKey : [queryKey];

  const { data, fetchNextPage, isLoading } = useInfiniteQuery({
    queryKey: infiniteScrollQueryKey,
    queryFn,
    enabled: queryEnabled,
    getNextPageParam: (lastPage) => {
      if (lastPage.data.pagination.is_last_page) return;
      return lastPage.data.pagination.page + 1;
    },
  });

  const items = data?.pages?.flatMap((page) => page.data.data);

  if (isLoading) return <Loading className={styles.loader} />;

  if (!items || !items.length) {
    return (
      <ListEmpty
        className={styles.emptyState}
        title={emptyStateTitle}
        titleVariant={emptyStateTitleVariant}
        body={emptyStateBody}
        bodyVariant={emptyStateBodyVariant}
        onButtonClick={handleEmptyStateClick}
        buttonColor={emptyStateButtonColor}
        buttonText={emptyStateButtonText}
        hasAddIcon={emptyStateHasAddIcon}
        hasButton={hasButton}
      />
    );
  }

  const { ListItem } = components;

  const ListItemElement = ListItem || null;

  return (
    <InfiniteScroll
      dataLength={items.length}
      next={() => fetchNextPage()}
      hasMore={!data?.pages.at(-1).data.pagination.is_last_page}
      loader={<Loading className={styles.loader} />}
    >
      {items.map((listItem) => (
        <ListItemElement key={`listItem-${listItem.key || listItem.id}`} {...listItem} />
      ))}
    </InfiniteScroll>
  );
};

InfiniteScrollList.propTypes = {
  components: PropTypes.shape({
    ListItem: PropTypes.elementType,
  }),
  emptyStateBody: PropTypes.string,
  emptyStateBodyVariant: PropTypes.string,
  emptyStateButtonColor: PropTypes.string,
  emptyStateButtonText: PropTypes.string,
  emptyStateHasAddIcon: PropTypes.bool,
  emptyStateTitle: PropTypes.string,
  emptyStateTitleVariant: PropTypes.string,
  handleEmptyStateClick: PropTypes.func,
  hasButton: PropTypes.bool,
  queryEnabled: PropTypes.bool,
  queryFn: PropTypes.func.isRequired,
  queryKey: PropTypes.string.isRequired,
};

export default React.memo(InfiniteScrollList);
